import type { Transport } from '@codemirror/lsp-client';

import type {
  LspContext,
  WorkerMessageToClient,
  WorkerMessageToServer,
} from './types';

/**
 * A Transport implementation that communicates with an LSP server
 * running in a Web Worker via postMessage.
 */
export class WorkerTransport implements Transport {
  private worker: Worker;
  private handlers: Set<(value: string) => void> = new Set();
  private isReady = false;
  private pendingMessages: string[] = [];
  private readyResolvers: Array<() => void> = [];

  constructor(worker: Worker) {
    console.log('[LSP-DEBUG] WorkerTransport: Constructor called');
    this.worker = worker;
    this.worker.addEventListener('message', this.handleWorkerMessage);
  }

  private handleWorkerMessage = (
    event: MessageEvent<WorkerMessageToClient>,
  ) => {
    const message = event.data;
    console.log(
      '[LSP-DEBUG] WorkerTransport: Received message from worker:',
      message.type,
    );

    if (message.type === 'ready') {
      console.log('[LSP-DEBUG] WorkerTransport: Worker is ready');
      this.isReady = true;
      // Send any messages that were queued before worker was ready
      for (const pending of this.pendingMessages) {
        this.sendToWorker({ type: 'lsp', payload: pending });
      }
      this.pendingMessages = [];
      // Resolve all waitForReady promises
      for (const resolve of this.readyResolvers) {
        resolve();
      }
      this.readyResolvers = [];
      return;
    }

    if (message.type === 'lsp') {
      // Forward LSP message to all subscribed handlers
      const parsed = JSON.parse(message.payload);
      console.log(
        '[LSP-DEBUG] WorkerTransport: LSP message from server:',
        parsed.method || parsed.id,
        parsed,
      );
      for (const handler of this.handlers) {
        handler(message.payload);
      }
    }
  };

  private sendToWorker(message: WorkerMessageToServer): void {
    this.worker.postMessage(message);
  }

  /**
   * Send an LSP message to the server.
   * If the worker isn't ready yet, queue the message.
   */
  send(message: string): void {
    const parsed = JSON.parse(message);
    console.log(
      '[LSP-DEBUG] WorkerTransport.send:',
      parsed.method || `response:${parsed.id}`,
      parsed,
      'handlers:',
      this.handlers.size,
    );
    if (this.isReady) {
      this.sendToWorker({ type: 'lsp', payload: message });
    } else {
      console.log(
        '[LSP-DEBUG] WorkerTransport.send: Worker not ready, queueing message',
      );
      this.pendingMessages.push(message);
    }
  }

  /**
   * Subscribe to LSP messages from the server.
   */
  subscribe(handler: (value: string) => void): void {
    console.log(
      '[LSP-DEBUG] WorkerTransport.subscribe: Adding handler, total handlers:',
      this.handlers.size + 1,
    );
    this.handlers.add(handler);
  }

  /**
   * Unsubscribe from LSP messages.
   */
  unsubscribe(handler: (value: string) => void): void {
    this.handlers.delete(handler);
  }

  /**
   * Wait for the worker to be ready.
   * Resolves immediately if already ready.
   */
  waitForReady(): Promise<void> {
    if (this.isReady) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      this.readyResolvers.push(resolve);
    });
  }

  /**
   * Update the LSP context (schema) in the worker.
   * This is separate from the LSP protocol and is used to update
   * the available variables/types for completion and validation.
   */
  updateContext(context: LspContext): void {
    console.log('[LSP-DEBUG] WorkerTransport.updateContext:', {
      documentUri: context.documentUri,
      schemaProperties: context.schema?.properties
        ? Object.keys(context.schema.properties)
        : 'none',
    });
    this.sendToWorker({ type: 'updateContext', payload: context });
  }

  /**
   * Terminate the worker and clean up.
   */
  destroy(): void {
    this.worker.removeEventListener('message', this.handleWorkerMessage);
    this.worker.terminate();
    this.handlers.clear();
  }
}
